
class CollapsibleMenuItem extends HTMLElement {
  constructor() {
    super();
    this.trigger = null;
    this.content = null;
  }

  connectedCallback() {
    this.trigger = this.querySelector('.collapsible-trigger');
    this.content = this.querySelector('.collapsible-content');

    if (!this.trigger || !this.content) return;

    this.content.style.overflow = 'hidden';
    this.content.style.transition = 'max-height 0.3s ease';

    if (this.classList.contains('open')) {
      this.content.style.display = 'block';
      // Use 'none' instead of scrollHeight — element may be inside a hidden panel
      // and scrollHeight would be 0, collapsing the content invisibly
      this.content.style.maxHeight = 'none';
      this.trigger.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('aria-hidden', 'false');
    } else {
      this.content.style.display = 'none';
      this.content.style.maxHeight = '0';
      this.trigger.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-hidden', 'true');
    }

    this.trigger.addEventListener('click', () => this.toggle());
  }

  toggle() {
    const isOpen = this.classList.contains('open');

    if (isOpen) {
      // Closing: measure current height, then animate to 0
      const height = this.content.scrollHeight;
      this.content.style.maxHeight = height + 'px';
      void this.content.offsetWidth; // force reflow
      this.content.style.maxHeight = '0';
      this.classList.remove('open');
      this.trigger.setAttribute('aria-expanded', 'false');
      this.content.setAttribute('aria-hidden', 'true');

      this.content.addEventListener(
        'transitionend',
        () => {
          if (!this.classList.contains('open')) {
            this.content.style.display = 'none';
          }
        },
        { once: true }
      );
    } else {
      // Opening: animate from 0 to full height
      this.classList.add('open');
      this.trigger.setAttribute('aria-expanded', 'true');
      this.content.setAttribute('aria-hidden', 'false');

      this.content.style.display = 'block';
      const height = this.content.scrollHeight;
      this.content.style.maxHeight = '0';
      void this.content.offsetWidth; // force reflow
      this.content.style.maxHeight = height + 'px';

      // After animation completes, set to 'none' so content can grow freely
      this.content.addEventListener(
        'transitionend',
        () => {
          if (this.classList.contains('open')) {
            this.content.style.maxHeight = 'none';
          }
        },
        { once: true }
      );
    }
  }
}

customElements.define('collapsible-menu-item', CollapsibleMenuItem);

// Auto-open the first collapsible item in each submenu panel
// Runs after DOM is ready; the first item may not have class="open" from the template
// if the first link in the loop is a plain link (no children).
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('.menu-drawer__inner-submenu').forEach(submenu => {
    const firstCollapsible = submenu.querySelector('collapsible-menu-item');
    if (firstCollapsible && !firstCollapsible.classList.contains('open')) {
      firstCollapsible.classList.add('open');
      const content = firstCollapsible.querySelector('.collapsible-content');
      const trigger = firstCollapsible.querySelector('.collapsible-trigger');
      if (content) {
        content.style.display = 'block';
        content.style.maxHeight = 'none'; // safe: scrollHeight may be 0 in hidden panel
      }
      if (trigger) trigger.setAttribute('aria-expanded', 'true');
    }
  });
});

// ── Mobile banner swipe + scrollbar indicator ────────────────────────────────
// iOS intercepts horizontal touch events when a parent has overflow-x:hidden,
// even with touch-action hints. We bypass this by manually driving scrollLeft
// on touchmove (same technique used internally by Swiper.js).
(function initBannerSwipe() {

  function getSlideWidth(banner) {
    const slide = banner.querySelector(
      '.mega-menu__banner--item, .mega-menu__banner--item-gallery'
    );
    if (!slide) return banner.clientWidth;
    // include the gap (0.8rem = ~6.8px at root 8.466px, but read computed gap)
    const gap = parseFloat(getComputedStyle(banner).gap) || 0;
    return slide.offsetWidth + gap;
  }

  function snapToNearestSlide(banner) {
    const slideWidth = getSlideWidth(banner);
    if (slideWidth <= 0) return;
    const index = Math.round(banner.scrollLeft / slideWidth);
    banner.scrollTo({ left: index * slideWidth, behavior: 'smooth' });
  }

  function updateScrollbar(banner) {
    const drag = banner._scrollbarDrag;
    if (!drag) return;
    const maxScroll = banner.scrollWidth - banner.clientWidth;
    if (maxScroll <= 0) return;
    const progress = banner.scrollLeft / maxScroll;
    const track = drag.parentElement;
    const dragWidth = drag.offsetWidth;
    const trackWidth = track.offsetWidth;
    drag.style.transform = 'translateX(' + (progress * (trackWidth - dragWidth)) + 'px)';
  }

  function injectScrollbar(banner) {
    if (banner._scrollbarDrag) return; // already done

    // Only show scrollbar when there are more than 2 banners
    const slides = banner.querySelectorAll('.mega-menu__banner--item, .mega-menu__banner--item-gallery');
    if (slides.length <= 2) return;

    // Calculate drag width as proportion of visible vs total width
    const ratio = banner.clientWidth / banner.scrollWidth;
    const pct = Math.max(15, Math.min(35, ratio * 100));

    const track = document.createElement('div');
    track.className = 'banner-scrollbar';

    const drag = document.createElement('div');
    drag.className = 'banner-scrollbar-drag';
    drag.style.width = pct + '%';

    track.appendChild(drag);
    banner.after(track);
    banner._scrollbarDrag = drag;
    updateScrollbar(banner);
  }

  function setupBanner(banner) {
    if (banner.dataset.swipeInit) return;
    banner.dataset.swipeInit = '1';

    let startX = 0, startY = 0, startScrollLeft = 0;
    let direction = null; // null = undecided, 'h' = horizontal, 'v' = vertical

    banner.addEventListener('touchstart', e => {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      startScrollLeft = banner.scrollLeft;
      direction = null;
    }, { passive: true });

    banner.addEventListener('touchmove', e => {
      const dx = e.touches[0].clientX - startX;
      const dy = e.touches[0].clientY - startY;

      // Wait until movement is large enough to determine direction
      if (direction === null) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        direction = Math.abs(dx) >= Math.abs(dy) ? 'h' : 'v';
      }

      if (direction === 'v') return; // vertical — let parent scroll naturally

      // Horizontal swipe: take full control, prevent parent scroll chain
      e.preventDefault();
      e.stopPropagation();
      banner.scrollLeft = startScrollLeft - dx;
      updateScrollbar(banner);
    }, { passive: false });

    banner.addEventListener('touchend', () => {
      if (direction === 'h') snapToNearestSlide(banner);
      direction = null;
    }, { passive: true });

    // Keep scrollbar in sync on any native scroll (e.g. mouse/trackpad on desktop)
    banner.addEventListener('scroll', () => updateScrollbar(banner), { passive: true });

    // Inject the scrollbar indicator below the banner
    // Use rAF so layout is settled and clientWidth/scrollWidth are accurate
    requestAnimationFrame(() => {
      injectScrollbar(banner);
    });
  }

  function initAll() {
    document.querySelectorAll('.mega-menu__banner-mobile').forEach(setupBanner);
  }

  // Run on DOMContentLoaded and immediately if DOM is already ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Also re-init when submenu panels slide open (banners may be in hidden panels)
  document.addEventListener('click', () => {
    setTimeout(initAll, 400); // after slide animation completes
  });

})();
